// Builds the new sidebar from the server-driven menu (/api/layout/GetAllMenu, MenuService.cs),
// so a user sees exactly the screens their rights allow in Classic. Only labels, grouping and order change:
// every item the server sends appears somewhere (items and groups the maps below don't know keep their classic labels).

export const MODULES = [
	{ id: 'Media', label: 'Media', base: '/Media', modelDefId: 1 },
	{ id: 'Production', label: 'Production', base: '/Production', modelDefId: 2 },
	{ id: 'Broadcast', label: 'Broadcast', base: '/Broadcast', modelDefId: 3 }
];

// Per-module screens, in sidebar order. The label is the new UI's name for the screen.
const WORK = [
	['Dashboard', 'Dashboard', 'dashboard'],
	['MIDocuments', 'Invoices', 'invoices'],
	['MADocuments', 'Archive', 'archive'],
	['PaymentDashboard', 'Payment dashboard', 'payments']
];
const ADMIN = [
	['Resources', 'User management', 'users'],
	['UserRoles', 'User groups', 'roles'],
	['GroupRules', 'Group rules', 'rules'],
	['CConfig', 'Company configuration', 'company'],
	['Integrations', 'Integrations', 'integrations']
];
const ADMIN_IDS = ADMIN.map(a => a[0]);

const norm = p => (p || '').toLowerCase().replace(/\/+$/, '');

// Sidebar names in sentence case, like the module screens ("Payment terms", "AP payments", "Audit log invoices"): words
// in capitals (AP, IO, PO) and words with digits keep their case (R14).
export function sentenceCase(label) {
	const words = String(label || '').trim().split(/(\s+)/);
	let first = true;
	return words.map(w => {
		if (!w.trim()) return w;
		if (first) { first = false; return w; }
		if (/^[A-Z0-9/&-]{2,}$/.test(w) || /\d/.test(w)) return w;
		return w.toLowerCase();
	}).join('');
}

export function moduleForPath(pathname, modules) {
	const p = norm(pathname);
	return modules.find(m => p === norm(m.base) || p.startsWith(norm(m.base) + '/')) || null;
}

// menu: the leftMenu array. pathname: the current URL path. relabel(item): optional label override (display names).
export function buildNav(menu, pathname, relabel) {
	const groups = (menu || []).filter(Boolean);
	const byId = id => groups.find(g => g.id === id);
	const label = item => sentenceCase((relabel && relabel(item)) || item.label);

	const modules = MODULES.filter(m => byId(m.id));
	const current = moduleForPath(pathname, modules) || modules[0] || null;
	const moduleItems = (current && byId(current.id) && byId(current.id).items) || [];

	const known = WORK.map(w => w[0]).concat('MIModels');
	const work = WORK
		.map(([id, text, icon]) => {
			const item = moduleItems.find(i => i.id === id);
			return item && { id, label: text, icon, path: item.path };
		})
		.filter(Boolean)
		.concat(moduleItems.filter(i => !known.includes(i.id)).map(i => ({ id: i.id, label: label(i), icon: 'invoices', path: i.path })));

	const setup = [];
	const models = moduleItems.find(i => i.id === 'MIModels');
	if (models) setup.push({ id: 'MIModels', label: 'Invoice models', icon: 'models', path: models.path });

	const system = (byId('SSetup') && byId('SSetup').items) || [];
	const masterData = system.filter(i => !ADMIN_IDS.includes(i.id)).map(i => ({ id: i.id, label: label(i), path: i.path }));
	const admin = ADMIN
		.map(([id, text, icon]) => {
			const item = system.find(i => i.id === id);
			return item && { id, label: text, icon, path: item.path };
		})
		.filter(Boolean);

	const reports = ((byId('Reports') && byId('Reports').items) || []).map(i => ({ id: i.id, label: label(i), path: i.path }));

	// Any other group the server adds keeps its own section.
	const knownGroups = MODULES.map(m => m.id).concat('SSetup', 'Reports');
	const others = groups.filter(g => !knownGroups.includes(g.id) && (g.items || []).length)
		.map(g => ({ title: g.label || g.id, items: [], group: { id: 'grp-' + g.id, label: g.label || g.id, icon: 'master', items: g.items.map(i => ({ id: i.id, label: label(i), path: i.path })) } }));

	const sections = [
		{ title: 'Work', items: work },
		{ title: 'Setup', items: setup, group: masterData.length ? { id: 'master', label: 'Master data', icon: 'master', items: masterData } : null },
		{ title: 'Administration', items: admin },
		{ title: 'Reports', items: [], group: reports.length ? { id: 'reports', label: 'All reports', crumb: 'Reports', icon: 'reports', items: reports } : null },
		...others
	].filter(s => s.items.length || s.group);

	return { modules, current, sections };
}

// Classic lists every module's screens at once. Switching module in the new sidebar keeps the same screen when the other
// module has it (e.g. Media Archive -> Broadcast Archive), and goes to that module's Dashboard otherwise.
export function moduleTarget(menu, pathname, target) {
	const groups = (menu || []).filter(Boolean);
	const from = moduleForPath(pathname, MODULES);
	const fromItems = (from && (groups.find(g => g.id === from.id) || {}).items) || [];
	const here = fromItems.find(i => norm(i.path) === norm(pathname));
	const toItems = (groups.find(g => g.id === target.id) || {}).items || [];
	const same = here && toItems.find(i => i.id === here.id);
	return same ? same.path : target.base + '/Dashboard/';
}

// Screens the server menu may not list (rights, or reached from another screen) still get a readable name.
const PAGE_NAMES = {
	managenonmappedinvoices: 'Non-mapped invoices', manageinvoicedocuments: 'Invoices', managearchivedocuments: 'Archive',
	paymentdashboard: 'Payment dashboard', manageinvoicemodels: 'Invoice models', vendorpaytomapping: 'Vendor site mapping',
	userinvoicestatuses: 'User statuses', clist: 'Clients', cllist: 'Clients', appayments: 'AP payments', auditinvoices: 'Audit log invoices'
};
const SECTION_OF = { setup: 'Master data', reports: 'Reports' };
export function pageName(pathname) {
	const raw = (pathname || '').split('/').filter(Boolean).pop() || '';
	if (PAGE_NAMES[raw.toLowerCase()]) return PAGE_NAMES[raw.toLowerCase()];
	const text = raw.replace(/^Manage(?=[A-Z])/, '').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[-_]+/g, ' ').trim();
	return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : '';
}

// Which kind of page this is, for page-level styling (data-section on the shell body): setup lists, reports, admin
// pages, or a module's screens. Anything else is undefined.
const ADMIN_PAGES = ['resources', 'userroles', 'grouprules', 'integrations', 'linkintegration', 'companyconfiguration'];
export function sectionOf(pathname) {
	const parts = (pathname || '').toLowerCase().split('/').filter(Boolean);
	if (!parts.length) return undefined;
	if (parts[0] === 'setup') return 'setup';
	if (parts[0] === 'reports') return 'reports';
	if (ADMIN_PAGES.includes(parts[0])) return 'admin';
	if (MODULES.some(m => m.id.toLowerCase() === parts[0])) return 'module';
	return undefined;
}

// Breadcrumb trail for the current path: [{label, path?}]
export function crumbsFor(nav, pathname) {
	const p = norm(pathname);
	for (const s of nav.sections) {
		const all = s.items.concat(s.group ? s.group.items : []);
		const hit = all.find(i => norm(i.path) === p);
		if (hit) {
			const inModule = s.title === 'Work' || hit.id === 'MIModels';
			const first = inModule && nav.current ? { label: nav.current.label, path: nav.current.base + '/Dashboard/' } : { label: s.group && s.group.items.includes(hit) ? s.group.crumb || s.group.label : s.title };
			return hit.id === 'Dashboard' ? [first, { label: 'Dashboard' }] : [first, { label: hit.label }];
		}
	}
	const inModule = moduleForPath(pathname, nav.modules);
	const segs = p.split('/').filter(Boolean);
	if (inModule) {
		const home = { label: inModule.label, path: inModule.base + '/Dashboard/' };
		return segs.length < 2 || segs[1] === 'dashboard' ? [home, { label: 'Dashboard' }] : [home, { label: pageName(pathname) }];
	}
	if (!segs.length) return [];
	if (ADMIN_PAGES.includes(segs[0])) return [{ label: 'Administration' }, { label: pageName(pathname) }];
	const section = segs.length > 1 ? SECTION_OF[segs[0]] : null;
	return section ? [{ label: section }, { label: pageName(pathname) }] : [{ label: pageName(pathname) }];
}

// The new UI's title for a module screen (Invoices, Archive, ... with the module beside it), or null where the screen's
// own title stays (the dashboard, which has its own header, and screens outside a module).
export function moduleTitle(nav, pathname) {
	const inModule = moduleForPath(pathname, nav.modules);
	if (!inModule) return null;
	const crumbs = crumbsFor(nav, pathname);
	const last = crumbs[crumbs.length - 1];
	if (!last || last.label === 'Dashboard') return null;
	return { title: last.label, module: inModule.label };
}
